import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Link as LinkIcon, Sparkles, Palette, Type, Layout, Shuffle, AlertCircle, CreditCard } from 'lucide-react';
import { useSite } from '../context/SiteContext';
import { aiService } from '../services/api';
import toast from 'react-hot-toast';
import { COLOR_THEMES } from '../constants/colorThemes';
import DomainNameModal from './DomainNameModal';

const FONT_OPTIONS = [
  { name: 'Inter', label: 'Modern', style: { fontFamily: 'Inter, sans-serif' } },
  { name: 'Urbanist', label: 'Bold', style: { fontFamily: 'Urbanist, sans-serif', fontWeight: '600' } },
  { name: 'Poppins', label: 'Friendly', style: { fontFamily: 'Poppins, sans-serif' } },
  { name: 'Playfair Display', label: 'Elegant', style: { fontFamily: 'Playfair Display, serif' } },
  { name: 'Roboto', label: 'Clean', style: { fontFamily: 'Roboto, sans-serif' } },
  { name: 'Montserrat', label: 'Strong', style: { fontFamily: 'Montserrat, sans-serif', fontWeight: '500' } },
  { name: 'Open Sans', label: 'Classic', style: { fontFamily: 'Open Sans, sans-serif' } },
  { name: 'Lato', label: 'Refined', style: { fontFamily: 'Lato, sans-serif' } },
  { name: 'Source Sans Pro', label: 'Professional', style: { fontFamily: 'Source Sans Pro, sans-serif' } },
  { name: 'Nunito', label: 'Rounded', style: { fontFamily: 'Nunito, sans-serif' } },
];

const SECTION_OPTIONS = [
  { id: 'header', name: 'Header & Navigation', required: true, icon: '🧭' },
  { id: 'hero', name: 'Hero Section', required: true, icon: '🚀' },
  { id: 'faq', name: 'FAQ/Questions', icon: '❓' },
  { id: 'services', name: 'Services/Features', icon: '⚙️' },
  { id: 'gallery', name: 'Gallery/Portfolio', icon: '🖼️' },
  { id: 'testimonials', name: 'Testimonials', icon: '💬' },
  { id: 'contact', name: 'Contact Information', icon: '📞' },
  { id: 'newsletter', name: 'Newsletter Signup', icon: '📧' },
  { id: 'footer', name: 'Footer', required: true, icon: '📄' }
];

const ModernOnboardingForm = () => {
  const navigate = useNavigate();
  const { createSite } = useSite();
  const [loading, setLoading] = useState(false);
  const [randomizing, setRandomizing] = useState(false);
  const [showDomainModal, setShowDomainModal] = useState(false);
  const [generationStep, setGenerationStep] = useState(0);
  const [planInfo, setPlanInfo] = useState(null);
  const [aiPreview, setAiPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [formData, setFormData] = useState({
    businessInfo: {
      name: '',
      description: '',
      location: '',
      existingWebLink: ''
    },
    design: {
      fontFamily: 'Inter',
      colorTheme: COLOR_THEMES[0],
      autoFont: true,
      autoColor: true
    },
    sections: ['header', 'hero', 'footer'],
    autoSections: true
  });

  // Load user's plan information
  useEffect(() => {
    const loadPlanInfo = async () => {
      try {
        const response = await fetch('/api/sites', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.planInfo) {
            setPlanInfo(data.planInfo);
          }
        }
      } catch (error) {
        console.error('Failed to load plan info:', error);
      }
    };
    
    loadPlanInfo();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      businessInfo: {
        ...prev.businessInfo,
        [field]: value
      }
    }));
  };

  const handleFontChange = (fontName) => {
    setFormData(prev => ({
      ...prev,
      design: {
        ...prev.design,
        fontFamily: fontName
      }
    }));
  };

  const handleColorThemeChange = (theme) => {
    setFormData(prev => ({
      ...prev,
      design: {
        ...prev.design,
        colorTheme: theme
      }
    }));
  };

  const handleSectionToggle = (sectionId) => {
    const section = SECTION_OPTIONS.find(s => s.id === sectionId);
    if (section?.required) return;

    setFormData(prev => {
      const isSelected = prev.sections.includes(sectionId);
      
      if (isSelected) {
        return {
          ...prev,
          sections: prev.sections.filter(id => id !== sectionId)
        };
      } else {
        const newSectionCount = prev.sections.length + 1;
        const maxSections = planInfo?.limits?.sections?.max || 6;
        
        if (maxSections !== 'Unlimited' && newSectionCount > maxSections) {
          toast.error(`Your plan allows maximum ${maxSections} sections per page`);
          return prev;
        }
        
        return {
          ...prev,
          sections: [...prev.sections, sectionId]
        };
      }
    });
  };

  const handleAutoToggle = (type, enabled) => {
    if (type === 'font') {
      setFormData(prev => ({
        ...prev,
        design: {
          ...prev.design,
          autoFont: enabled
        }
      }));
    } else if (type === 'color') {
      setFormData(prev => ({
        ...prev,
        design: {
          ...prev.design,
          autoColor: enabled
        }
      }));
    } else if (type === 'sections') {
      setFormData(prev => ({
        ...prev,
        autoSections: enabled
      }));
    }
  };

  const handleRandomize = () => {
    setShowDomainModal(true);
  };

  const handleDomainConfirm = async (domainName) => {
    setShowDomainModal(false);
    setRandomizing(true);
    
    try {
      toast.loading('AI is generating random business data...', { id: 'randomizing' });
      
      const response = await fetch('/api/ai/generate-random-business', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ domainName })
      });

      if (response.ok) {
        const data = await response.json();
        
        const cleanString = (str) => {
          if (typeof str !== 'string') return str;
          return str.trim().replace(/,$/, '').replace(/\.$/, '');
        };
        
        setFormData(prev => ({
          ...prev,
          businessInfo: {
            name: cleanString(data.name),
            description: data.description?.trim(),
            location: cleanString(data.location),
            existingWebLink: data.socialLink?.trim()
          }
        }));
        
        toast.success('Random business data generated!', { id: 'randomizing' });
      } else {
        toast.error('Failed to generate random data', { id: 'randomizing' });
      }
    } catch (error) {
      console.error('Randomize error:', error);
      toast.error('Failed to generate random data', { id: 'randomizing' });
    } finally {
      setRandomizing(false);
    }
  };

  const isFormValid = () => {
    return formData.businessInfo.name.trim() && formData.businessInfo.description.trim();
  };

  // Get AI preview recommendations when business info is complete
  const getAiPreview = async () => {
    if (!isFormValid() || previewLoading) return;
    
    // Only get preview if at least one auto option is enabled
    const hasAutoEnabled = formData.design.autoFont || formData.design.autoColor || formData.autoSections;
    if (!hasAutoEnabled) {
      setAiPreview(null);
      return;
    }
    
    setPreviewLoading(true);
    
    try {
      const response = await fetch('/api/ai/get-design-preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          businessInfo: formData.businessInfo,
          availableThemes: COLOR_THEMES,
          availableFonts: FONT_OPTIONS,
          availableSections: SECTION_OPTIONS
        })
      });

      if (response.ok) {
        const preview = await response.json();
        setAiPreview(preview);
      } else {
        console.warn('Failed to get AI preview');
        setAiPreview(null);
      }
    } catch (error) {
      console.error('AI preview error:', error);
      setAiPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Trigger preview when business info or auto settings change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      getAiPreview();
    }, 1000); // Debounce for 1 second

    return () => clearTimeout(timeoutId);
  }, [formData.businessInfo.name, formData.businessInfo.description, formData.design.autoFont, formData.design.autoColor, formData.autoSections]);

  // Get AI design recommendations
  const getAIDesignRecommendations = async (formData) => {
    // If we already have preview data, use it for better consistency
    if (aiPreview && (formData.design.autoFont || formData.design.autoColor || formData.autoSections)) {
      console.log('Using AI preview data for recommendations');
      
      return {
        ...formData,
        design: {
          ...formData.design,
          fontFamily: formData.design.autoFont ? aiPreview.recommendedFont : formData.design.fontFamily,
          colorTheme: formData.design.autoColor ? aiPreview.recommendedThemeObject?.colors || aiPreview.recommendedThemeObject : formData.design.colorTheme,
          colorThemeName: formData.design.autoColor ? aiPreview.recommendedTheme : formData.design.colorTheme.name,
          imageCorners: 'rounded-lg',
          buttonCorners: 'rounded-md',
          maxWidth: 'normal',
          headingWeight: 'font-bold',
          headingSize: 3,
          bottomBorder: true
        },
        sections: formData.autoSections ? aiPreview.recommendedSections : formData.sections
      };
    }

    try {
      const response = await fetch('/api/ai/get-design-recommendations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          businessInfo: formData.businessInfo,
          availableThemes: COLOR_THEMES,
          availableFonts: FONT_OPTIONS
        })
      });

      if (!response.ok) {
        throw new Error('AI recommendations failed');
      }

      const recommendations = await response.json();
      
      return {
        ...formData,
        design: {
          ...formData.design,
          fontFamily: formData.design.autoFont ? recommendations.recommendedFont : formData.design.fontFamily,
          colorTheme: formData.design.autoColor ? recommendations.recommendedTheme.colors : formData.design.colorTheme,
          colorThemeName: formData.design.autoColor ? recommendations.recommendedTheme.name : formData.design.colorTheme.name,
          imageCorners: recommendations.designCustomizations?.imageCorners || 'rounded-lg',
          buttonCorners: recommendations.designCustomizations?.buttonCorners || 'rounded-md',
          maxWidth: recommendations.designCustomizations?.maxWidth || 'normal',
          headingWeight: recommendations.designCustomizations?.headingWeight || 'font-bold',
          headingSize: recommendations.designCustomizations?.headingSize || 3,
          bottomBorder: recommendations.designCustomizations?.bottomBorder !== undefined ? recommendations.designCustomizations.bottomBorder : true
        },
        sections: formData.autoSections ? recommendations.recommendedSections : formData.sections
      };
    } catch (error) {
      console.warn('AI recommendations failed, using fallback logic');
      
      // Fallback to simple logic
      const description = formData.businessInfo.description.toLowerCase();
      
      // Basic font selection
      let selectedFont = 'Inter';
      if (description.includes('law') || description.includes('professional')) {
        selectedFont = 'Source Sans Pro';
      } else if (description.includes('creative') || description.includes('design')) {
        selectedFont = 'Playfair Display';
      } else if (description.includes('tech') || description.includes('software')) {
        selectedFont = 'Roboto';
      } else if (description.includes('food') || description.includes('friendly')) {
        selectedFont = 'Nunito';
      }

      // Basic color selection
      let selectedTheme = COLOR_THEMES[0];
      if (description.includes('nature') || description.includes('eco')) {
        selectedTheme = COLOR_THEMES.find(t => t.name === 'Emerald Forest');
      } else if (description.includes('creative') || description.includes('art')) {
        selectedTheme = COLOR_THEMES.find(t => t.name === 'Lavender Dream');
      } else if (description.includes('luxury') || description.includes('premium')) {
        selectedTheme = COLOR_THEMES.find(t => t.name === 'Plum Velvet');
      }

      // Basic section selection
      let selectedSections = ['header', 'hero', 'footer'];
      if (description.includes('service') || description.includes('offer')) {
        selectedSections.push('services');
      }
      if (description.includes('about') || description.includes('story')) {
        selectedSections.push('faq');
      }
      if (description.includes('portfolio') || description.includes('gallery')) {
        selectedSections.push('gallery');
      }
      if (description.includes('review') || description.includes('testimonial')) {
        selectedSections.push('testimonials');
      }
      if (description.includes('contact') || description.includes('appointment')) {
        selectedSections.push('contact');
      }

      return {
        ...formData,
        design: {
          ...formData.design,
          fontFamily: formData.design.autoFont ? selectedFont : formData.design.fontFamily,
          colorTheme: formData.design.autoColor ? selectedTheme : formData.design.colorTheme,
          colorThemeName: formData.design.autoColor ? selectedTheme.name : formData.design.colorTheme.name,
          imageCorners: 'rounded-lg',
          buttonCorners: 'rounded-md',
          maxWidth: 'normal',
          headingWeight: 'font-bold',
          headingSize: 3,
          bottomBorder: true
        },
        sections: formData.autoSections ? selectedSections : formData.sections
      };
    }
  };

  // Generate navigation items for multi-page structure
  const generateNavigationItems = (sections) => {
    const menuLabels = {
      'hero': 'Home',
      'faq': 'FAQ',
      'services': 'Services', 
      'gallery': 'Portfolio',
      'testimonials': 'Reviews',
      'contact': 'Contact',
      'newsletter': 'Newsletter'
    };
    
    return sections
      .filter(s => s !== 'header' && s !== 'footer')
      .slice(0, 6)
      .map(sectionId => ({
        label: menuLabels[sectionId] || sectionId.charAt(0).toUpperCase() + sectionId.slice(1),
        target: sectionId,
        type: 'section'
      }));
  };

  // Generate header content
  const generateHeaderContent = (finalFormData) => {
    const primaryCTA = finalFormData.sections.includes('contact') ? 'Contact Us' : 
                      finalFormData.sections.includes('services') ? 'Our Services' : 'Get Started';
    
    const ctaLink = finalFormData.sections.includes('contact') ? '#contact' :
                   finalFormData.sections.includes('services') ? '#services' : '#hero';

    const navigation = generateNavigationItems(finalFormData.sections);

    return {
      businessName: finalFormData.businessInfo.name.replace(/^"|"$/g, ''),
      navigation: navigation.map(item => ({
        label: item.label,
        link: `#${item.target}`
      })),
      primaryCTA: {
        text: primaryCTA,
        link: ctaLink
      },
      invertedColors: true,
      showButton: false,
      style: 'clean',
      layout: 'horizontal'
    };
  };

  // Create modern multi-page site
  const createModernSite = async (finalFormData) => {
    try {
      // Generate SEO
      const seoResponse = await aiService.generateSEO(
        finalFormData.businessInfo,
        finalFormData.sections
      );

      // Create modern multi-page site structure
      const siteData = {
        businessInfo: finalFormData.businessInfo,
        design: finalFormData.design,
        seo: seoResponse.data.seo,
        
        // Multi-page structure
        pages: [
          {
            id: "home",
            name: "Home", 
            slug: "index",
            sections: finalFormData.sections.map(sectionId => ({
              id: sectionId,
              type: sectionId,
              content: sectionId === 'header' ? generateHeaderContent(finalFormData) : {},
              visible: true,
              order: SECTION_OPTIONS.findIndex(s => s.id === sectionId)
            })),
            design: finalFormData.design,
            seo: {
              title: `${finalFormData.businessInfo.name} - Home`,
              description: finalFormData.businessInfo.description,
              keywords: seoResponse.data.seo.keywords
            }
          }
        ],
        
        // Navigation structure
        navigation: {
          items: generateNavigationItems(finalFormData.sections)
        },
        
        // Legacy compatibility
        sections: finalFormData.sections.map(sectionId => ({
          id: sectionId,
          type: sectionId,
          content: sectionId === 'header' ? generateHeaderContent(finalFormData) : {},
          visible: true,
          order: SECTION_OPTIONS.findIndex(s => s.id === sectionId)
        }))
      };

      return await createSite(siteData);
    } catch (error) {
      console.error('Site creation failed:', error);
      throw error;
    }
  };

  // Generate sections with improved error handling
  const generateSectionsWithFallback = async (siteId, finalFormData) => {
    const nonHeaderSections = finalFormData.sections.filter(s => s !== 'header');
    
    const supportedSections = {
      'hero': { required: true, fallback: 'Welcome to our business' },
      'faq': { required: false, fallback: 'Frequently asked questions' },
      'services': { required: false, fallback: 'Our services and offerings' },
      'gallery': { required: false, fallback: 'Our work portfolio' },
      'testimonials': { required: false, fallback: 'Customer testimonials' },
      'contact': { required: false, fallback: 'Contact information' },
      'newsletter': { required: false, fallback: 'Newsletter signup' },
      'footer': { required: true, fallback: 'Company footer' }
    };
    
    const generationResults = [];
    
    for (const sectionType of nonHeaderSections) {
      try {
        const sectionConfig = supportedSections[sectionType];
        
        if (sectionConfig) {
          console.log(`🎨 Generating ${sectionType} section...`);
          
          const response = await fetch(`/api/sites/${siteId}/sections/${sectionType}/regenerate`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              businessInfo: finalFormData.businessInfo,
              fallbackContent: sectionConfig.fallback
            })
          });

          if (response.ok) {
            console.log(`✅ ${sectionType} section generated successfully`);
            generationResults.push({ section: sectionType, status: 'success' });
          } else {
            console.warn(`⚠️ Failed to generate ${sectionType} section`);
            
            if (sectionConfig.required) {
              console.log(`🔄 Using fallback for required section: ${sectionType}`);
              generationResults.push({ section: sectionType, status: 'fallback' });
            } else {
              generationResults.push({ section: sectionType, status: 'failed' });
            }
          }
        } else {
          console.log(`ℹ️ Skipping ${sectionType} section (not supported)`);
          generationResults.push({ section: sectionType, status: 'skipped' });
        }
      } catch (error) {
        console.error(`❌ Error generating ${sectionType}:`, error);
        
        const sectionConfig = supportedSections[sectionType];
        if (sectionConfig?.required) {
          generationResults.push({ section: sectionType, status: 'fallback', error: error.message });
        } else {
          generationResults.push({ section: sectionType, status: 'error', error: error.message });
        }
      }
    }
    
    const successful = generationResults.filter(r => r.status === 'success').length;
    const fallbacks = generationResults.filter(r => r.status === 'fallback').length;
    const total = generationResults.filter(r => r.status !== 'skipped').length;
    console.log(`🎯 Generation complete: ${successful}/${total} successful, ${fallbacks} fallbacks`);
    
    return generationResults;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isFormValid()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setGenerationStep(0);
    
    try {
      const steps = [
        'Getting AI design recommendations...',
        'Creating modern multi-page site structure...',
        'Generating content sections...',
        'Finalizing your website...'
      ];

      // Progress through steps
      const stepInterval = setInterval(() => {
        setGenerationStep(prev => Math.min(prev + 1, steps.length - 1));
      }, 1500);
      
      // Phase 1: Get AI recommendations
      const finalFormData = await getAIDesignRecommendations(formData);
      
      // Phase 2: Create modern site
      const createdSite = await createModernSite(finalFormData);
      
      // Phase 3: Generate sections
      const generationResults = await generateSectionsWithFallback(createdSite._id, finalFormData);
      
      clearInterval(stepInterval);
      setGenerationStep(0);
      
      // Show success
      const successful = generationResults.filter(r => r.status === 'success').length;
      const total = generationResults.filter(r => r.status !== 'skipped').length;
      toast.success(`🎉 Your modern website is ready! (${successful}/${total} sections generated)`, { 
        duration: 4000,
        style: {
          background: '#10B981',
          color: 'white',
        }
      });
      
      // Set completion flags
      sessionStorage.setItem(`fromOnboarding_${createdSite._id}`, 'true');
      sessionStorage.setItem(`onboardingComplete_${createdSite._id}`, Date.now().toString());
      
      // Navigate to editor
      navigate(`/editor/${createdSite._id}`);
      
    } catch (error) {
      console.error('Failed to create site:', error);
      setGenerationStep(0);
      toast.error('Failed to create website. Please try again!', {
        style: {
          background: '#EF4444',
          color: 'white',
        }
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors px-3 py-2 rounded-md hover:bg-gray-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </button>
            
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 rounded-md p-2">
                <Sparkles className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Create Modern Website</h1>
                <p className="text-sm text-gray-500">Multi-page AI builder</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">What's your website about?</h1>
              <p className="text-gray-600">Tell us about your business and we'll create a modern multi-page website.</p>
            </div>
            <button
              type="button"
              onClick={handleRandomize}
              disabled={randomizing}
              className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition"
            >
              <Shuffle className={`w-4 h-4 mr-2 ${randomizing ? 'animate-spin' : ''}`} />
              {randomizing ? 'Generating...' : 'Randomize'}
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Business Information */}
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                rows={4}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                placeholder="e.g. Modern web design agency specializing in e-commerce solutions. 5+ years experience creating conversion-focused websites."
                value={formData.businessInfo.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business name
              </label>
              <input
                type="text"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                value={formData.businessInfo.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Language <span className="text-red-500">*</span>
              </label>
              <select 
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                value={formData.businessInfo.language || 'en'}
                onChange={(e) => handleInputChange('language', e.target.value)}
                required
              >
                <option value="en">🇺🇸 English</option>
                <option value="tr">🇹🇷 Turkish</option>
                <option value="es">🇪🇸 Spanish</option>
                <option value="fr">🇫🇷 French</option>
                <option value="de">🇩🇪 German</option>
                <option value="it">🇮🇹 Italian</option>
                <option value="pt">🇵🇹 Portuguese</option>
                <option value="ru">🇷🇺 Russian</option>
                <option value="zh">🇨🇳 Chinese</option>
                <option value="ja">🇯🇵 Japanese</option>
                <option value="ko">🇰🇷 Korean</option>
                <option value="ar">🇸🇦 Arabic</option>
                <option value="hi">🇮🇳 Hindi</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                This will be your website's main language. You can add more languages later in site settings.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business location <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm pr-10"
                  value={formData.businessInfo.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <MapPin className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Existing web link <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <input
                type="url"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                placeholder="Paste your existing website or social media"
                value={formData.businessInfo.existingWebLink}
                onChange={(e) => handleInputChange('existingWebLink', e.target.value)}
              />
            </div>
          </div>

          {/* Design & Style */}
          <div className="space-y-6">
            <h2 className="text-lg font-medium text-gray-900">Design & Style</h2>
            
            {/* Font Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Typography
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.design.autoFont}
                    onChange={(e) => handleAutoToggle('font', e.target.checked)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 mr-2"
                  />
                  <span className="text-sm text-gray-600">Auto</span>
                </label>
              </div>
              
              {!formData.design.autoFont && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {FONT_OPTIONS.map((font) => (
                  <button
                    key={font.name}
                    type="button"
                    className={`p-3 border border-gray-300 rounded-lg text-left hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition ${
                      formData.design.fontFamily === font.name
                        ? 'ring-2 ring-purple-500 border-purple-500 bg-purple-50'
                        : ''
                    }`}
                    onClick={() => handleFontChange(font.name)}
                  >
                    <div style={font.style} className="mb-1">
                      <div className="text-lg font-semibold text-gray-900">Aa</div>
                      <div className="text-xs text-gray-600 truncate" style={font.style}>Sample</div>
                    </div>
                    <div className="text-xs font-medium text-gray-900">{font.label}</div>
                  </button>
                ))}
                </div>
              )}
              
              {formData.design.autoFont && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="bg-blue-100 rounded-full p-2 mr-3">
                      <Type className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-900">AI will select the perfect font</p>
                      <p className="text-xs text-blue-700">Based on your business type and industry</p>
                      {previewLoading && (
                        <p className="text-xs text-blue-600 mt-1">🔍 Analyzing your business...</p>
                      )}
                      {aiPreview && !previewLoading && (
                        <div className="mt-2 p-2 bg-white rounded border">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                Recommended: {aiPreview.recommendedFontObject?.label || aiPreview.recommendedFont}
                              </p>
                              <p className="text-xs text-gray-600">{aiPreview.fontReasoning}</p>
                            </div>
                            <div style={aiPreview.recommendedFontObject?.style} className="text-xl font-semibold text-gray-700">
                              Aa
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Color Theme Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  Color Theme
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.design.autoColor}
                    onChange={(e) => handleAutoToggle('color', e.target.checked)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 mr-2"
                  />
                  <span className="text-sm text-gray-600">Auto</span>
                </label>
              </div>
              
              {!formData.design.autoColor && (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {COLOR_THEMES.map((theme) => (
                  <button
                    key={theme.name}
                    type="button"
                    className={`p-2 border border-gray-300 rounded-lg hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition ${
                      formData.design.colorTheme.name === theme.name
                        ? 'ring-2 ring-purple-500 border-purple-500 bg-purple-50'
                        : ''
                    }`}
                    onClick={() => handleColorThemeChange(theme)}
                  >
                    <div className="space-y-2">
                      <div className="flex space-x-1">
                        <div 
                          className="w-3 h-3 rounded border border-gray-200" 
                          style={{ backgroundColor: theme.colors.primary }}
                        />
                        <div 
                          className="w-3 h-3 rounded border border-gray-200" 
                          style={{ backgroundColor: theme.colors.secondary }}
                        />
                        <div 
                          className="w-3 h-3 rounded border border-gray-200" 
                          style={{ backgroundColor: theme.colors.accent }}
                        />
                      </div>
                      
                      <div 
                        className="w-full h-6 rounded border border-gray-200 overflow-hidden"
                        style={{ backgroundColor: theme.colors.background }}
                      >
                        <div 
                          className="h-1 w-full"
                          style={{ backgroundColor: theme.colors.primary }}
                        />
                      </div>

                      <div className="text-center">
                        <div className="text-xs font-medium text-gray-900 truncate">{theme.name}</div>
                      </div>
                    </div>
                  </button>
                ))}
                </div>
              )}
              
              {formData.design.autoColor && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="bg-blue-100 rounded-full p-2 mr-3">
                      <Palette className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-900">AI will choose matching colors</p>
                      <p className="text-xs text-blue-700">Based on your business description and mood</p>
                      {previewLoading && (
                        <p className="text-xs text-blue-600 mt-1">🔍 Analyzing your business...</p>
                      )}
                      {aiPreview && !previewLoading && (
                        <div className="mt-2 p-2 bg-white rounded border">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                Recommended: {aiPreview.recommendedThemeObject?.name || aiPreview.recommendedTheme}
                              </p>
                              <p className="text-xs text-gray-600">{aiPreview.themeReasoning}</p>
                            </div>
                            <div className="flex space-x-1">
                              <div 
                                className="w-4 h-4 rounded border border-gray-200" 
                                style={{ backgroundColor: aiPreview.recommendedThemeObject?.colors?.primary }}
                              />
                              <div 
                                className="w-4 h-4 rounded border border-gray-200" 
                                style={{ backgroundColor: aiPreview.recommendedThemeObject?.colors?.secondary }}
                              />
                              <div 
                                className="w-4 h-4 rounded border border-gray-200" 
                                style={{ backgroundColor: aiPreview.recommendedThemeObject?.colors?.accent }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Content Sections */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Content Sections.</h2>
                {planInfo && (
                  <div className="flex items-center space-x-2 mt-1">
                    <CreditCard className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-500">
                      {planInfo.plan.name} Plan: Maximum {planInfo.limits.sections.max} sections
                    </span>
                  </div>
                )}
              </div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.autoSections}
                  onChange={(e) => handleAutoToggle('sections', e.target.checked)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 mr-2"
                />
                <span className="text-sm text-gray-600">Auto</span>
              </label>
            </div>
            
            {!formData.autoSections && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {SECTION_OPTIONS.map((section) => {
                const isSelected = formData.sections.includes(section.id);
                const maxSections = planInfo?.limits?.sections?.max || 6;
                const atLimit = maxSections !== 'Unlimited' && formData.sections.length >= maxSections;
                const isDisabled = section.required ? false : (atLimit && !isSelected);
                
                return (
                  <label
                    key={section.id}
                    className={`relative flex items-center p-3 border border-gray-300 rounded-lg transition ${
                      isDisabled && !section.required
                        ? 'opacity-50 cursor-not-allowed bg-gray-50'
                        : 'cursor-pointer hover:border-gray-400 focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-purple-500'
                    } ${
                      isSelected
                        ? 'ring-2 ring-purple-500 border-purple-500 bg-purple-50'
                        : ''
                    } ${section.required ? 'ring-2 ring-blue-200 bg-blue-50' : ''}`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={isSelected}
                      onChange={() => handleSectionToggle(section.id)}
                      disabled={section.required || isDisabled}
                    />
                  
                  <div className="flex items-center space-x-3 w-full">
                    <div className="text-lg">{section.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900">{section.name}</div>
                      {section.required ? (
                        <div className="text-xs text-blue-600 font-medium">Required</div>
                      ) : isDisabled ? (
                        <div className="text-xs text-red-500 font-medium">Plan limit reached</div>
                      ) : (
                        <div className="text-xs text-gray-500">Optional</div>
                      )}
                    </div>
                    <div className={`flex-shrink-0 w-4 h-4 border border-gray-300 rounded flex items-center justify-center ${
                      isSelected
                        ? 'bg-purple-600 border-purple-600'
                        : isDisabled
                        ? 'bg-gray-200 border-gray-200'
                        : 'bg-white'
                    }`}>
                      {isSelected && (
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                      {isDisabled && !isSelected && (
                        <AlertCircle className="w-3 h-3 text-gray-400" />
                      )}
                    </div>
                  </div>
                </label>
                );
              })}
              </div>
            )}

            {formData.autoSections && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <div className="bg-blue-100 rounded-full p-2 mr-3">
                    <Layout className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900">AI will choose relevant sections</p>
                    <p className="text-xs text-blue-700">Based on your business needs and services</p>
                    {previewLoading && (
                      <p className="text-xs text-blue-600 mt-1">🔍 Analyzing your business...</p>
                    )}
                    {aiPreview && !previewLoading && (
                      <div className="mt-2 p-2 bg-white rounded border">
                        <div>
                          <p className="text-sm font-medium text-gray-900 mb-1">
                            Recommended sections:
                          </p>
                          <div className="flex flex-wrap gap-1 mb-2">
                            {aiPreview.recommendedSections?.map(sectionId => {
                              const section = SECTION_OPTIONS.find(s => s.id === sectionId);
                              return (
                                <span key={sectionId} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
                                  {section?.icon} {section?.name || sectionId}
                                </span>
                              );
                            })}
                          </div>
                          <p className="text-xs text-gray-600">{aiPreview.sectionsReasoning}</p>
                          {aiPreview.businessAnalysis && (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                              <p className="text-xs text-gray-500">
                                <strong>Business type:</strong> {aiPreview.businessAnalysis.type} • 
                                <strong> Personality:</strong> {aiPreview.businessAnalysis.personality} • 
                                <strong> Audience:</strong> {aiPreview.businessAnalysis.targetAudience}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {!formData.autoSections && (
              <div className="text-center space-y-2">
                <div className="inline-flex items-center px-3 py-1 text-sm text-purple-700 bg-purple-100 rounded-full">
                  ✨ <strong className="ml-1">{formData.sections.length}</strong> sections selected
                </div>
                {planInfo && (
                  <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
                    <span>Plan limit: {planInfo.limits.sections.max} sections</span>
                    {planInfo.limits.sections.max !== 'Unlimited' && (
                      <div className="flex items-center space-x-1">
                        <div className="w-16 bg-gray-200 rounded-full h-1.5">
                          <div 
                            className={`h-1.5 rounded-full transition-all ${
                              formData.sections.length >= planInfo.limits.sections.max
                                ? 'bg-red-500'
                                : 'bg-blue-500'
                            }`}
                            style={{
                              width: `${Math.min((formData.sections.length / planInfo.limits.sections.max) * 100, 100)}%`
                            }}
                          ></div>
                        </div>
                        <span className={formData.sections.length >= planInfo.limits.sections.max ? 'text-red-500 font-medium' : ''}>
                          {formData.sections.length}/{planInfo.limits.sections.max}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center pt-8 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </button>
            
            <button
              type="submit"
              disabled={loading || !isFormValid()}
              className="inline-flex items-center px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Modern Site...
                </>
              ) : (
                <>
                  Create Modern Website
                  <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                </>
              )}
            </button>
          </div>
          
          {!isFormValid() && (
            <p className="text-sm text-gray-500 text-center mt-4">
              Please fill in your business name and description to continue
            </p>
          )}
        </form>
      </main>

      {/* Modern Generation Processing Modal */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              {/* AI Animation */}
              <div className="mx-auto mb-6 w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-white animate-pulse" />
              </div>
              
              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Creating Your Modern Multi-Page Website
              </h3>
              
              {/* Current Step */}
              <p className="text-sm text-gray-600 mb-6">
                {[
                  'Getting AI design recommendations...',
                  'Creating modern multi-page site structure...',
                  'Generating content sections...',
                  'Finalizing your website...'
                ][generationStep]}
              </p>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                <div 
                  className="bg-gradient-to-r from-purple-500 to-blue-500 h-3 rounded-full transition-all duration-1000 ease-out"
                  style={{ 
                    width: `${((generationStep + 1) / 4) * 100}%` 
                  }}
                ></div>
              </div>
              
              {/* Step Counter */}
              <p className="text-xs text-gray-500">
                Step {generationStep + 1} of 4
              </p>
              
              {/* Modern Features Notice */}
              <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
                <div className="flex items-center justify-center mb-2">
                  <span className="text-purple-600 font-medium text-sm">✨ Modern Multi-Page Architecture</span>
                </div>
                <div className="text-xs text-gray-600 space-y-1">
                  <div>• Multi-page site structure</div>
                  <div>• AI-driven design recommendations</div>
                  <div>• Improved error handling</div>
                  <div>• Future-ready architecture</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Domain Name Modal */}
      <DomainNameModal
        isOpen={showDomainModal}
        onClose={() => setShowDomainModal(false)}
        onConfirm={handleDomainConfirm}
      />
    </div>
  );
};

export default ModernOnboardingForm;